import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useOutputPanelShortcut } from "../../hooks/useOutputPanelShortcut";
import { clientSendContext } from "../../lib/clientSendContext";
import { emitErrorToast } from "../../lib/errorToast";
import { executionContentPath } from "../../lib/apiPaths";
import { apiUrl } from "../../lib/tenantOrigin";
import { uploadToolCallOutput } from "../../lib/toolCallOutputs";
import {
  approvePlan,
  askQuestion,
  bash,
  type ChatTool,
  connectChatToApp,
  connectToApp,
  editFile,
  execute,
  readFile,
  readSkillFile,
  runSkillScript,
  showExecutionOutput,
  type ExecutionOutputView,
  useSkill,
  webFetch,
  webSearch,
  writeFile,
} from "../../tools";
import { Chat, type ChatProps } from "../Chat";
import { ChatMessageNav } from "../Chat/ChatMessageNav";
import { FilePreviewPanel } from "../AttachmentHoverPreview";
import { ImportWorkflowModal } from "../ImportWorkflowModal";
import { Resizable } from "../Resizable";
import { readOnlyStyle } from "./readOnlyStyle";
import { ChatActionsMenu } from "./ChatActionsMenu";
import { deriveChatOutputs } from "./chatOutputs";
import { DeriveWorkflowButton } from "./DeriveWorkflowButton";
import { OutputSwitcher } from "./OutputSwitcher";
import { OutputsToggleButton } from "./OutputsToggleButton";
import { PersonalChatConnectorsBar } from "./PersonalChatConnectorsBar";
import { PersonalChatVolumesButton } from "./PersonalChatVolumesButton";
import { chatSuggestions } from "./chatSlashSuggestions";
import { greetingName, NewChatEmptyState } from "./NewChatEmptyState";
import { pickChatIconColor } from "./pickChatIconColor";
import { useQuery } from "../../hooks/useQuery";
import { mentionConnectorCatalog } from "../../hooks/useMentions";

type ChatRow = {
  id: string;
  respondingAt: number | null;
  lastLoopHeartbeatAt: number | null;
  awaitingAction: boolean | null;
  entries: readonly NonNullable<
    Parameters<typeof Chat>[0]["entries"]
  >[number][];
};

type Props = {
  personalChatId: string;
  chat: ChatRow;
  pending?: boolean;
  readOnly?: boolean;
  onForkChat?: (() => void) | undefined;
  onDownloadChat?: (() => void) | undefined;
  onCloseChat?: (() => void) | undefined;
  onTogglePin?: (() => void) | undefined;
  pinned?: boolean;
};

const Container = styled("div")`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  container-type: inline-size;
`;

const Workspace = styled("div")`
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

const OutputPane = styled("div")`
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: 8px;
  box-sizing: border-box;
`;

// Zero-width so there's no gutter: the output pane butts against the chat and
// the grip zone overflows into the output pane's own left edge.
const ResizeHandle = styled("div")`
  position: relative;
  width: 0;
  height: 100%;

  &[data-orientation="vertical"] {
    width: 100%;
    height: 0;
  }
`;

const ResizeGripZone = styled("div")`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;

  [data-orientation="vertical"] & {
    top: 0;
    bottom: auto;
    left: 0;
    right: 0;
    width: auto;
    height: 12px;
    cursor: ns-resize;
  }
`;

// The shared drag-indicator grip (see MaximizedStep / Workflow): hidden until
// the handle is hovered, revealed by Resizable's HandleContainer.
const ResizeGrip = styled("div")`
  width: 2px;
  height: 32px;
  border-radius: 10px;
  background: var(--border-alpha);

  [data-orientation="vertical"] & {
    width: 32px;
    height: 2px;
  }
`;

const ScrollArea = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  container-type: inline-size;
  margin-bottom: 32px;
  transition: margin-bottom 160ms ease-out;
  @container (max-width: 768px) {
    margin-bottom: 8px;
  }
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChatWrap = styled("div")`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;
  transition: padding 160ms ease-out;
  @container (max-width: 768px) {
    padding: 0 8px;
  }
`;

const NavWrap = styled("div")`
  position: absolute;
  top: 0;
  bottom: 32px;
  right: 14px;
  width: 20px;
  pointer-events: none;
`;

const RightControls = styled("div")`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AppsShell = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  box-sizing: border-box;
`;

// ChatWorkspace remounts per tab (keyed by personalChatId), so the open output
// and the dragged panel width are kept here to survive tab switches.
const openOutputByChatId = new Map<string, ExecutionOutputView>();
const outputSizeByChatId = new Map<string, number>();

export function ChatWorkspace({
  personalChatId,
  chat,
  pending,
  readOnly,
  onForkChat,
  onDownloadChat,
  onCloseChat,
  onTogglePin,
  pinned,
}: Props) {
  const isMobile = useIsMobile();
  const z = useZero();
  const [currentUser] = useQuery(queries.currentUser());
  const userName = greetingName(currentUser?.name ?? "");
  const [personalChat] = useQuery(queries.loadPersonalChat({ personalChatId }));
  const mentionConnectors = useMemo(() => {
    const seen = new Set<string>();
    return (personalChat?.appUsages ?? []).flatMap((row) => {
      const app = row.connectedApp;
      if (!app || seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [personalChat?.appUsages]);
  const [tenantConnectorsRaw] = useQuery(queries.connectorsIndex());
  const mentionTenantConnectors = useMemo(() => {
    const seen = new Set<string>();
    return tenantConnectorsRaw.flatMap((app) => {
      if (seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [tenantConnectorsRaw]);
  const [pendingZip, setPendingZip] = useState<File | null>(null);
  const [output, setOutput] = useState<ExecutionOutputView | null>(
    () => openOutputByChatId.get(personalChatId) ?? null
  );
  const [outputOpen, setOutputOpen] = useState(() =>
    openOutputByChatId.has(personalChatId)
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const lastOutputRef = useRef<ExecutionOutputView | null>(null);
  const showOutput = useCallback((next: ExecutionOutputView) => {
    setOutput(next);
  }, []);
  const closeOutput = useCallback(() => setOutputOpen(false), []);
  const handleOutputResize = useCallback(
    (size: number | undefined) => {
      if (size == null) outputSizeByChatId.delete(personalChatId);
      else outputSizeByChatId.set(personalChatId, size);
    },
    [personalChatId]
  );
  // Mount the panel collapsed, then expand on the next frame so the Resizable's
  // width transition animates it in; the close collapse unmounts on transitionend.
  useEffect(() => {
    if (!output) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setOutputOpen(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [output]);
  const outputs = useMemo(
    () => deriveChatOutputs(chat.entries as ChatEntryWithSubTables[]),
    [chat.entries]
  );
  useEffect(() => {
    if (output) lastOutputRef.current = output;
  }, [output]);
  useEffect(() => {
    if (output && outputOpen) openOutputByChatId.set(personalChatId, output);
    else openOutputByChatId.delete(personalChatId);
  }, [personalChatId, output, outputOpen]);
  const toggleOutput = useCallback(() => {
    if (outputOpen) {
      closeOutput();
      return;
    }
    const remembered = lastOutputRef.current;
    if (
      remembered &&
      outputs.some((o) => o.executionId === remembered.executionId)
    ) {
      showOutput(remembered);
      return;
    }
    const first = outputs[0];
    if (first) {
      showOutput({
        executionId: first.executionId,
        fileName: first.fileName,
        mediaType: "",
        sizeBytes: 0,
      });
    }
  }, [outputOpen, outputs, showOutput, closeOutput]);
  useOutputPanelShortcut(toggleOutput, outputs.length > 0);
  const tools: readonly ChatTool[] = useMemo(
    () => [
      askQuestion,
      approvePlan,
      bash({ getStepColor: () => undefined }),
      connectChatToApp({ personalChatId }),
      connectToApp({ personalChatId }),
      editFile({ getStepColor: () => undefined }),
      execute,
      showExecutionOutput({
        respondingAt: chat.respondingAt,
        onShowOutput: showOutput,
      }),
      readFile({ getStepColor: () => undefined }),
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      useSkill,
      readSkillFile,
      runSkillScript,
      webFetch,
      webSearch,
      writeFile({ getStepColor: () => undefined }),
    ],
    [chat.respondingAt, personalChatId, showOutput]
  );

  useEffect(() => {
    if (pending) return;
    z.mutate(sharedMutators.touchPersonalChat({ id: personalChatId }));
  }, [personalChatId, pending, z]);

  const handleSend = useCallback<ChatProps["onSend"]>(
    (payload, onError) => {
      const result = z.mutate(
        sharedMutators.sendPersonalChatMessage({
          chatId: chat.id,
          entryId: nanoid(12),
          text: payload.text,
          isDictated: payload.isDictated,
          effort: payload.effort,
          chatModel: payload.chatModel,
          attachments: payload.attachments,
          interruptEntryId: payload.interruptEntryId,
          ...clientSendContext(),
        })
      );
      void result.server.then((r) => {
        if (r.type === "error") onError();
      });
    },
    [chat.id, z]
  );

  const handleInterrupt = () => {
    z.mutate(
      sharedMutators.interruptPersonalChat({
        entryId: nanoid(12),
        chatId: chat.id,
      })
    );
  };

  const handleProvideToolResult = useCallback<ChatProps["onProvideToolResult"]>(
    async (entryId, output, isError, options) => {
      try {
        const outputId = await uploadToolCallOutput({
          chatId: chat.id,
          entryId,
          output,
        });
        const result = await z.mutate(
          sharedMutators.providePersonalChatToolResult({
            chatId: chat.id,
            entryId,
            outputId,
            isError,
            pauseAfter: options?.pauseAfter,
            chatModel: options?.chatModel,
          })
        ).server;
        if (result.type === "error") throw new Error(result.error.message);
        return true;
      } catch {
        emitErrorToast({
          icon: "circle-off",
          message: "The tool result couldn’t be saved.",
          buttonLabel: "Dismiss",
        });
        return false;
      }
    },
    [chat.id, z]
  );

  const chatStarted = chat.entries.length > 0;

  const chatPane = (
    <Container ref={containerRef}>
      <RightControls style={readOnly ? readOnlyStyle : undefined}>
        <DeriveWorkflowButton personalChatId={personalChatId} />
        {chatStarted && (
          <AppsShell>
            <PersonalChatConnectorsBar personalChatId={personalChatId} />
            {outputs.length > 0 && (
              <OutputsToggleButton
                isOpen={outputOpen}
                onToggle={toggleOutput}
              />
            )}
            <ChatActionsMenu
              onFork={onForkChat}
              onDownload={onDownloadChat}
              onClose={onCloseChat}
              onTogglePin={onTogglePin}
              pinned={pinned}
            />
          </AppsShell>
        )}
        <PersonalChatVolumesButton />
      </RightControls>
      <ScrollArea ref={scrollRef}>
        <ChatWrap>
          <Chat
            key={chat.id}
            modelScope={{ type: "personal" }}
            onZipFile={setPendingZip}
            entries={chat.entries}
            writeChatId={chat.id}
            draftKey={personalChatId}
            onSend={handleSend}
            onInterrupt={handleInterrupt}
            onProvideToolResult={handleProvideToolResult}
            isCurrentUserChat={!readOnly}
            canWrite={!readOnly}
            tools={tools}
            mentionConnectors={mentionConnectors}
            mentionTenantConnectors={mentionTenantConnectors}
            mentionConnectorCatalog={mentionConnectorCatalog}
            mentionConnectorContextLabel="On this chat"
            suggestions={chatSuggestions}
            placeholder="Ask anything across your tools and data"
            emptyState={({ fillInput, visible }) => (
              <NewChatEmptyState
                onPick={fillInput}
                iconColor={`var(--${pickChatIconColor(personalChatId)}-600)`}
                userName={userName}
                seed={personalChatId}
                visible={visible}
              />
            )}
            soundsEnabled={!readOnly}
            respondingAt={chat.respondingAt}
            lastLoopHeartbeatAt={chat.lastLoopHeartbeatAt}
            awaitingAction={chat.awaitingAction ?? false}
            planModeEnabled={false}
            outerScroll
            outerScrollRef={scrollRef}
          />
          {pendingZip && (
            <ImportWorkflowModal
              file={pendingZip}
              onClose={() => setPendingZip(null)}
            />
          )}
        </ChatWrap>
      </ScrollArea>
      <NavWrap>
        <ChatMessageNav
          entries={chat.entries as ChatEntryWithSubTables[]}
          scrollContainer={scrollRef}
          wrapperRef={containerRef}
        />
      </NavWrap>
    </Container>
  );

  return (
    <Workspace
      ref={workspaceRef}
      style={isMobile ? { flexDirection: "column" } : undefined}
      onTransitionEnd={(e) => {
        if (
          !outputOpen &&
          (e.propertyName === "width" || e.propertyName === "height")
        ) {
          setOutput(null);
        }
      }}
    >
      {output ? (
        <Resizable
          containerRef={workspaceRef}
          orientation={isMobile ? "vertical" : "horizontal"}
          minSize={isMobile ? 240 : 360}
          minOppositeSize={isMobile ? 240 : 360}
          defaultSize="calc(100% / 3)"
          initialSize={outputSizeByChatId.get(personalChatId)}
          onSizeChange={handleOutputResize}
          overrideSize={outputOpen ? undefined : 0}
          Handle={
            <ResizeHandle
              data-orientation={isMobile ? "vertical" : "horizontal"}
            >
              <ResizeGripZone>
                <ResizeGrip data-drag-indicator />
              </ResizeGripZone>
            </ResizeHandle>
          }
          first={chatPane}
          second={
            <OutputPane
              style={{
                ...(isMobile ? { paddingTop: 0 } : { paddingLeft: 0 }),
                opacity: outputOpen ? 1 : 0,
                transition: "opacity 160ms ease-out",
              }}
            >
              <FilePreviewPanel
                url={`${apiUrl()}${executionContentPath(output.executionId, "output")}`}
                fileName={output.fileName}
                sizeBytes={output.sizeBytes}
                interactive
                heading={
                  <OutputSwitcher
                    entries={chat.entries as ChatEntryWithSubTables[]}
                    currentExecutionId={output.executionId}
                    currentFileName={output.fileName}
                    onShowOutput={showOutput}
                  />
                }
                onClose={closeOutput}
              />
            </OutputPane>
          }
        />
      ) : (
        chatPane
      )}
    </Workspace>
  );
}
